import { zodResolver } from '@hookform/resolvers/zod';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import { Box, CircularProgress, Dialog, DialogContent, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { PersonAvatar } from './AccountMenu';
import { getUiLanguage, tr } from '../lib/i18n';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { api, ApiError, setAccessToken, uploadFile } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { EMPLOYEE_TYPE, fmtDate } from '../lib/labels';
import { Button } from './button';
import { InfoRow } from './org';
import { LanguageButton } from './LanguageButton';
import { PasswordField } from './text-field';
import { useToast } from './Toast';

const Schema = z
  .object({
    currentPassword: z.string().min(1, tr('مطلوب', 'Required')),
    newPassword: z.string().min(8, tr('8 أحرف على الأقل', 'At least 8 characters')).refine((p) => /[A-Za-z]/.test(p) && /\d/.test(p), tr('يجب أن تحتوي حروفاً وأرقاماً', 'Must contain letters and numbers')),
    confirm: z.string(),
  })
  .refine((v) => v.newPassword === v.confirm, { path: ['confirm'], message: tr('غير متطابقة', 'Passwords do not match') });

/** Changing the password rotates the session: the server returns a fresh access token. */
export function ChangePasswordForm({ onDone }: { onDone?: () => void }) {
  const { reload } = useAuth();
  const { toast, toastError } = useToast();
  const { register, handleSubmit, formState, setError, reset } = useForm<z.infer<typeof Schema>>({
    resolver: zodResolver(Schema),
    defaultValues: { currentPassword: '', newPassword: '', confirm: '' },
  });
  const submit = handleSubmit(async (v) => {
    try {
      const r = await api<{ accessToken: string }>('/auth/change-password', { method: 'POST', body: { currentPassword: v.currentPassword, newPassword: v.newPassword } });
      setAccessToken(r.accessToken);
      await reload();
      reset();
      toast(tr('تم تغيير كلمة المرور', 'Password changed'));
      onDone?.();
    } catch (e) {
      const fe = e instanceof ApiError ? e.fieldErrors() : {};
      if (fe.currentPassword) setError('currentPassword', { message: e instanceof Error ? e.message : '' });
      else if (fe.newPassword) setError('newPassword', { message: e instanceof Error ? e.message : '' });
      else toastError(e);
    }
  });
  return (
    <Stack component="form" spacing={2} onSubmit={submit} noValidate>
      <PasswordField label={tr('كلمة المرور الحالية', 'Current password')} autoComplete="current-password" {...register('currentPassword')} error={!!formState.errors.currentPassword} helperText={formState.errors.currentPassword?.message} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <PasswordField fullWidth sx={{ flex: 1 }} label={tr('كلمة المرور الجديدة', 'New password')} autoComplete="new-password" {...register('newPassword')} error={!!formState.errors.newPassword} helperText={formState.errors.newPassword?.message} />
        <PasswordField fullWidth sx={{ flex: 1 }} label={tr('تأكيد كلمة المرور', 'Confirm password')} autoComplete="new-password" {...register('confirm')} error={!!formState.errors.confirm} helperText={formState.errors.confirm?.message} />
      </Stack>
      {formState.isDirty && (
        <Button type="submit" variant="contained" loading={formState.isSubmitting}>{tr('حفظ كلمة المرور', 'Save password')}</Button>
      )}
    </Stack>
  );
}

/** Crop and shrink while preserving transparent backgrounds. */
async function toAvatarImage(file: File): Promise<File> {
  const bmp = await createImageBitmap(file);
  const side = Math.min(bmp.width, bmp.height);
  const out = Math.min(512, side);
  const canvas = document.createElement('canvas');
  canvas.width = out;
  canvas.height = out;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(bmp, (bmp.width - side) / 2, (bmp.height - side) / 2, side, side, 0, 0, out, out);
  bmp.close();
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/png'));
  if (!blob) throw new Error(tr('تعذر تجهيز الصورة', 'Could not process the image'));
  return new File([blob], 'avatar.png', { type: 'image/png' });
}

/** Own profile picture: click to upload (crop to square), or remove. */
function AvatarEditor() {
  const { me, reload } = useAuth();
  const { toast, toastError } = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  if (!me) return null;
  const u = me.user;
  const set = async (fileId: string | null) => {
    await api('/me/avatar', { method: 'PUT', body: { fileId } });
    await reload();
  };
  const onPick = async (f: File | undefined) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) return toast(tr('اختر صورة JPG أو PNG أو WebP', 'Choose a JPG, PNG or WebP image'), 'warning');
    setBusy(true);
    try {
      const id = await uploadFile(await toAvatarImage(f), '/me/avatar/presign');
      await set(id);
      toast(tr('تم تحديث الصورة', 'Photo updated'));
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await set(null);
      toast(tr('تمت إزالة الصورة', 'Photo removed'));
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Stack alignItems="center" spacing={0.5}>
      <Box sx={{ position: 'relative' }}>
        <PersonAvatar name={u.full_name} seed={u.id} src={u.avatarUrl} size={88} themed />
        {busy && (
          <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: 'rgba(255,255,255,.6)', display: 'grid', placeItems: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        )}
        <Tooltip title={tr('تغيير الصورة', 'Change photo')}>
          <IconButton
            size="small"
            onClick={() => input.current?.click()}
            disabled={busy}
            aria-label={tr('تغيير الصورة', 'Change photo')}
            sx={{ position: 'absolute', bottom: -2, insetInlineEnd: -2, bgcolor: 'primary.main', color: 'primary.contrastText', border: 2, borderColor: 'background.paper', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            <PhotoCameraOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <input
          ref={input}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          hidden
          onChange={(e) => {
            void onPick(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </Box>
      {u.avatarUrl && (
        <Button size="small" onClick={remove} disabled={busy}>{tr('إزالة الصورة', 'Remove photo')}</Button>
      )}
    </Stack>
  );
}

export function ProfileDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { me } = useAuth();
  if (!me) return null;
  const u = me.user;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Stack alignItems="center" spacing={1}>
            <AvatarEditor />
            <Typography variant="h6">{u.full_name}</Typography>
            <Typography variant="body2" color="text.secondary">
              <span dir="ltr">@{u.username}</span>
              {' · '}{EMPLOYEE_TYPE[u.role_code] ?? (getUiLanguage() === 'en' ? u.role_name_en ?? u.role_name : u.role_name)}
              {u.employee_type !== u.role_code && <>{' · '}{EMPLOYEE_TYPE[u.employee_type] ?? u.employee_type}</>}
            </Typography>
          </Stack>
          <InfoRow label={tr('البريد', 'Email')}>{u.email ?? '—'}</InfoRow>
          <InfoRow label={tr('الهاتف', 'Phone')}><span dir="ltr">{u.phone ?? '—'}</span></InfoRow>
          <InfoRow label={tr('رمز الموظف', 'Employee code')}><span dir="ltr">{u.employee_code ?? '—'}</span></InfoRow>
          <InfoRow label={tr('آخر دخول', 'Last login')}>{fmtDate(u.last_login_at)}</InfoRow>
          <Divider />
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="subtitle2">{tr('لغة الواجهة', 'Interface language')}</Typography>
              <Typography variant="caption" color="text.secondary">{tr('تغيير لغة واتجاه النظام', 'Change the system language and direction')}</Typography>
            </Box>
            <LanguageButton labeled />
          </Stack>
          <Divider />
          {open && <ChangePasswordForm onDone={onClose} />}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
